import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { useSearchParams } from 'react-router-dom';
import {
    listEarlyAccessApplications,
    approveEarlyAccessApplication,
    updateEarlyAccessQuota,
    type EarlyAccessApplication,
    type EarlyAccessStatusFilter,
} from '../api/earlyAccess';
import './EarlyAccessApplicants-new.css';

/* ============================================================
   EarlyAccessApplicants-new.tsx
   Super-admin panel listing public early-access applications
   with one-click approval. Layout mirrors SuperAdminAnalytics.
   ============================================================ */

type ToastKind = 'success' | 'error';

interface Toast
{
    kind: ToastKind;
    message: string;
}

interface QuotaInput
{
    tokens: string;
    expiryDays: string;
}

type QuotaPayload = { beta_free_token_quota: number; beta_free_quota_expiry_days: number };

function formatDate(value: string | null): string
{
    if (!value)
    {
        return '—';
    }
    try
    {
        return new Date(value).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }
    catch
    {
        return value;
    }
}

function statusBadgeClass(status: EarlyAccessApplication['status']): string
{
    switch (status)
    {
        case 'approved':
            return 'ea-badge ea-badge-approved';
        case 'rejected':
            return 'ea-badge ea-badge-rejected';
        default:
            return 'ea-badge ea-badge-pending';
    }
}

const FILTER_TABS: { label: string; value: EarlyAccessStatusFilter }[] = [
    { label: 'Pending', value: 'pending' },
    { label: 'All', value: 'all' },
];

const EarlyAccessApplicantsPage: React.FC = () =>
{
    const [searchParams] = useSearchParams();
    const initialFilter = searchParams.get('mode') === 'quota' ? 'all' : 'pending';
    const targetApplicationId = Number(searchParams.get('applicationId') || 0) || null;
    const [filter, setFilter] = useState<EarlyAccessStatusFilter>(initialFilter);
    const [items, setItems] = useState<EarlyAccessApplication[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<number | null>(null);
    const [toast, setToast] = useState<Toast | null>(null);
    const [quotaInputs, setQuotaInputs] = useState<Record<number, QuotaInput>>({});

    const loadApplications = useCallback(async () =>
    {
        setLoading(true);
        setError(null);
        try
        {
            const res = await listEarlyAccessApplications({
                status: filter,
                page: 1,
                page_size: 100,
            });
            const orderedItems = targetApplicationId
                ? [...res.items].sort((a, b) =>
                    (a.id === targetApplicationId ? -1 : 0) + (b.id === targetApplicationId ? 1 : 0))
                : res.items;
            setItems(orderedItems);
            setQuotaInputs((prev) =>
            {
                const next = { ...prev };
                orderedItems.forEach((item) =>
                {
                    if (!next[item.id])
                    {
                        next[item.id] = {
                            tokens: item.beta_free_token_quota ? String(item.beta_free_token_quota) : '',
                            expiryDays: item.beta_free_quota_expiry_days ? String(item.beta_free_quota_expiry_days) : '',
                        };
                    }
                });
                return next;
            });
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'Failed to load early-access applications');
        }
        finally
        {
            setLoading(false);
        }
    }, [filter, targetApplicationId]);

    useEffect(() =>
    {
        loadApplications();
    }, [loadApplications]);

    /* Auto-dismiss toast after a few seconds */
    useEffect(() =>
    {
        if (!toast)
        {
            return;
        }
        const handle = window.setTimeout(() => setToast(null), 4000);
        return () => window.clearTimeout(handle);
    }, [toast]);

    function hasQuotaInput(application: EarlyAccessApplication): boolean
    {
        const input = quotaInputs[application.id] || { tokens: '', expiryDays: '' };
        return Boolean(input.tokens.trim() || input.expiryDays.trim());
    }

    function parseQuotaInput(application: EarlyAccessApplication): QuotaPayload | null
    {
        const input = quotaInputs[application.id] || { tokens: '', expiryDays: '' };
        const tokens = Number(input.tokens);
        const expiryDays = Number(input.expiryDays);
        if (!Number.isInteger(tokens) || tokens <= 0 || !Number.isInteger(expiryDays) || expiryDays <= 0)
        {
            setToast({ kind: 'error', message: 'Enter raw token quota and expiry days as positive whole numbers.' });
            return null;
        }
        return {
            beta_free_token_quota: tokens,
            beta_free_quota_expiry_days: expiryDays,
        };
    }

    function updateQuotaInput(applicationId: number, field: keyof QuotaInput, value: string): void
    {
        const digitsOnly = value.replace(/[^\d]/g, '');
        setQuotaInputs((prev) => ({
            ...prev,
            [applicationId]: {
                tokens: prev[applicationId]?.tokens || '',
                expiryDays: prev[applicationId]?.expiryDays || '',
                [field]: digitsOnly,
            },
        }));
    }

    async function handleApprove(application: EarlyAccessApplication): Promise<void>
    {
        if (approvingId !== null)
        {
            return;
        }
        const betaEnabled = Boolean(application.beta_quota_enabled);
        const quotaPayload = betaEnabled && hasQuotaInput(application)
            ? parseQuotaInput(application)
            : undefined;
        if (betaEnabled && hasQuotaInput(application) && !quotaPayload)
        {
            return;
        }
        setApprovingId(application.id);
        try
        {
            const updated = await approveEarlyAccessApplication(application.id, quotaPayload);
            setItems((prev) =>
            {
                if (filter === 'pending')
                {
                    return prev.filter((item) => item.id !== updated.id);
                }
                return prev.map((item) => (item.id === updated.id ? updated : item));
            });
            setToast({ kind: 'success', message: `Approved ${updated.email} — signup link emailed.` });
        }
        catch (err: any)
        {
            const detail = err?.response?.data?.detail || 'Failed to approve application';
            setToast({ kind: 'error', message: detail });
        }
        finally
        {
            setApprovingId(null);
        }
    }

    async function handleSaveQuota(application: EarlyAccessApplication): Promise<void>
    {
        if (approvingId !== null)
        {
            return;
        }
        const quotaPayload = parseQuotaInput(application);
        if (!quotaPayload)
        {
            return;
        }
        setApprovingId(application.id);
        try
        {
            const updated = await updateEarlyAccessQuota(application.id, quotaPayload);
            setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            setToast({ kind: 'success', message: `Updated beta quota for ${updated.email}.` });
        }
        catch (err: any)
        {
            const detail = err?.response?.data?.detail || 'Failed to update beta quota';
            setToast({ kind: 'error', message: detail });
        }
        finally
        {
            setApprovingId(null);
        }
    }

    const totalCount = useMemo(() => items.length, [items]);
    const betaQuotaVisible = useMemo(
        () => items.some((item) => Boolean(item.beta_quota_enabled)),
        [items],
    );

    return (
        <div className="ea-page">
            <header className="ea-page-header">
                <div>
                    <h1 className="ea-page-title">Early Access Applicants</h1>
                    <p className="ea-page-subtitle">
                        Review pending requests and grant access with a single click.
                    </p>
                </div>
                <div className="ea-filter-switcher" aria-label="Filter applications">
                    {FILTER_TABS.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            className={`ea-filter-pill ${filter === tab.value ? 'ea-filter-pill-active' : ''}`}
                            onClick={() => setFilter(tab.value)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </header>

            {toast && (
                <div className={`ea-toast ea-toast-${toast.kind}`} role="status">
                    {toast.message}
                </div>
            )}

            <section className="ea-card">
                <div className="ea-card-header">
                    <span className="ea-card-title">
                        {filter === 'pending' ? 'Pending applications' : 'All applications'}
                    </span>
                    <span className="ea-card-count">{loading ? '…' : `${totalCount} total`}</span>
                </div>

                {loading ? (
                    <div className="ea-card-empty">
                        <Spinner animation="border" size="sm" /> Loading…
                    </div>
                ) : error ? (
                    <div className="ea-card-empty ea-card-error">{error}</div>
                ) : items.length === 0 ? (
                    <div className="ea-card-empty">
                        {filter === 'pending'
                            ? 'No pending applications. You\'re all caught up.'
                            : 'No applications yet.'}
                    </div>
                ) : (
                    <div className="ea-table-wrapper">
                        <table className="ea-table" data-responsive-cards="true">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Company</th>
                                    <th>Reffered By</th>
                                    <th>Applied</th>
                                    {betaQuotaVisible && <th>Free Tokens</th>}
                                    {betaQuotaVisible && <th>Expiry Days</th>}
                                    <th>Status</th>
                                    <th className="ea-table-actions-col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id}>
                                        <td data-label="Name">{item.full_name}</td>
                                        <td data-label="Email">{item.email}</td>
                                        <td data-label="Company">{item.company_name}</td>
                                        <td className="ea-table-notes" title={item.notes || ''} data-label="Reffered By">
                                            {item.notes || '—'}
                                        </td>
                                        <td data-label="Applied">{formatDate(item.created_at)}</td>
                                        {betaQuotaVisible && (
                                            <td data-label="Free Tokens">
                                                {item.beta_quota_enabled ? (
                                                    <input
                                                        className="ea-quota-input"
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={quotaInputs[item.id]?.tokens || ''}
                                                        onChange={(e) => updateQuotaInput(item.id, 'tokens', e.target.value)}
                                                        placeholder="1000000"
                                                        disabled={approvingId === item.id || Boolean(item.beta_quota_locked)}
                                                    />
                                                ) : (
                                                    <span className="ea-table-muted">-</span>
                                                )}
                                            </td>
                                        )}
                                        {betaQuotaVisible && (
                                            <td data-label="Expiry Days">
                                                {item.beta_quota_enabled ? (
                                                    <input
                                                        className="ea-quota-input ea-quota-input-small"
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={quotaInputs[item.id]?.expiryDays || ''}
                                                        onChange={(e) => updateQuotaInput(item.id, 'expiryDays', e.target.value)}
                                                        placeholder="30"
                                                        disabled={approvingId === item.id || Boolean(item.beta_quota_locked)}
                                                    />
                                                ) : (
                                                    <span className="ea-table-muted">-</span>
                                                )}
                                            </td>
                                        )}
                                        <td data-label="Status">
                                            <span className={statusBadgeClass(item.status)}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="ea-table-actions" data-label="Actions" data-card-actions="true">
                                            {item.status === 'pending' ? (
                                                <button
                                                    type="button"
                                                    className="ea-approve-btn"
                                                    onClick={() => handleApprove(item)}
                                                    disabled={approvingId === item.id}
                                                >
                                                    {approvingId === item.id ? (
                                                        <Spinner as="span" animation="border" size="sm" />
                                                    ) : (
                                                        item.beta_quota_enabled && hasQuotaInput(item) ? 'Approve with Quota' : 'Approve'
                                                    )}
                                                </button>
                                            ) : item.status === 'approved' && item.beta_quota_enabled ? (
                                                <button
                                                    type="button"
                                                    className="ea-approve-btn ea-secondary-btn"
                                                    onClick={() => handleSaveQuota(item)}
                                                    disabled={approvingId === item.id || Boolean(item.beta_quota_locked)}
                                                    title={item.beta_quota_locked ? 'Beta quota cannot be changed after a successful subscription payment.' : undefined}
                                                >
                                                    {approvingId === item.id ? (
                                                        <Spinner as="span" animation="border" size="sm" />
                                                    ) : (
                                                        item.beta_quota_locked ? 'Paid billing active' : 'Save Quota'
                                                    )}
                                                </button>
                                            ) : (
                                                <span className="ea-table-muted">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default EarlyAccessApplicantsPage;
